import { useMemo, useState } from 'react';
import Link from 'next/link';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';

import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Divider,
  Heading,
  Modal,
  Paragraph,
  Table,
  Tag,
  Text,
  ToggleButton,
  ToggleButtonGroup,
} from '@zillow/constellation-10';

import { AdproductsConstants } from '@zg-rentals/rental-revenue-components';

import { ROUTES } from '../../constants/urls';
import { PACKAGE_TIER_ORDER } from './constants';

import type { ListingCompDto, ListingRankDto } from '@zg-rentals/ts-rental-manager-api-client';

type HigherTierPackagesModalProps = {
  onClose: () => void;
  topComps: Array<ListingCompDto>;
  rankings: Array<ListingRankDto>;
  listingAlias: string;
};

type CompetitorMetric = 'leads' | 'views' | 'impressions' | 'adImpressions' | 'adClicks';
type PackageTone = 'blue' | 'green' | 'purple' | 'red';

const METRIC_RANK_FIELD: Record<CompetitorMetric, keyof ListingRankDto> = {
  leads: 'leadsRank',
  views: 'viewsRank',
  impressions: 'impressionsRank',
  adImpressions: 'adImpressionsRank',
  adClicks: 'adClicksRank',
};

const competitorMetricLabels: Record<CompetitorMetric, string> = {
  leads: 'Leads',
  views: 'Views',
  impressions: 'Impressions',
  adImpressions: 'Ad impressions',
  adClicks: 'Ad clicks',
};

const PACKAGE_TONE: Record<string, PackageTone> = {
  Signature: 'purple',
  'Premium Plus': 'purple',
  Premium: 'blue',
  Enhanced: 'green',
  Base: 'red',
};

const PACKAGE_PIE_COLOR: Record<string, string> = {
  Signature: '#e6a8ff',
  'Premium Plus': '#e6a8ff',
  Premium: '#9acdff',
  Enhanced: '#9ff17b',
  Base: '#ff7f73',
};

const renderPackageLabel = (props: unknown) => {
  const {
    name = '',
    value = 0,
    x = 0,
    y = 0,
    textAnchor = 'start',
  } = props as {
    name?: string;
    value?: number;
    x?: number;
    y?: number;
    textAnchor?: 'start' | 'middle' | 'end';
  };

  return (
    <text
      x={x}
      y={y}
      fill="var(--colors-text-default)"
      stroke="none"
      textAnchor={textAnchor}
      dominantBaseline="central"
    >
      {`${name} ${value}%`}
    </text>
  );
};

const HigherTierPackagesModal = ({ onClose, topComps, rankings, listingAlias }: HigherTierPackagesModalProps) => {
  const [competitorMetric, setCompetitorMetric] = useState<CompetitorMetric>('leads');

  const rankedEntries = useMemo(() => {
    const rankField = METRIC_RANK_FIELD[competitorMetric];
    return topComps
      .map((comp) => {
        const alias = String(comp.listingAlias) === String(listingAlias) ? listingAlias : comp.listingAlias;
        const ranking = rankings.find((r) => String(r.listingAlias) === String(alias));
        const rankValue = ranking ? (ranking[rankField] as number) : Infinity;
        const displayName = comp.propertyName || comp.address;
        const packageName =
          AdproductsConstants.ProductTypes[comp.currentProductType as keyof typeof AdproductsConstants.ProductTypes] ??
          comp.currentProductType;
        const packageTone = PACKAGE_TONE[packageName] ?? ('blue' as PackageTone);
        const isCurrentListing = String(comp.listingAlias) === String(listingAlias);
        return { comp, rankValue, displayName, packageName, packageTone, isCurrentListing };
      })
      .sort((a, b) => a.rankValue - b.rankValue);
  }, [competitorMetric, topComps, listingAlias, rankings]);

  const packageDistribution = useMemo(() => {
    const counts = new Map<string, number>();
    for (const comp of topComps) {
      const name =
        AdproductsConstants.ProductTypes[comp.currentProductType as keyof typeof AdproductsConstants.ProductTypes] ??
        comp.currentProductType;
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    const total = topComps.length;
    return [...counts.entries()]
      .map(([name, count]) => ({
        label: name,
        value: total > 0 ? Math.round((count / total) * 100) : 0,
        color: PACKAGE_PIE_COLOR[name] ?? '#9acdff',
        tierOrder:
          PACKAGE_TIER_ORDER[
            Object.entries(AdproductsConstants.ProductTypes).find(
              ([, v]) => v === name,
            )?.[0] as keyof typeof PACKAGE_TIER_ORDER
          ] ?? 0,
      }))
      .sort((a, b) => b.tierOrder - a.tierOrder);
  }, [topComps]);

  return (
    <Modal.Root open={true} onOpenChange={(o) => !o && onClose()} size="md">
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Header>
              <Heading level={2} textStyle="heading-sm">
                Lead rank
              </Heading>
            </Modal.Header>
            <Divider />
            <Modal.Body>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '800' }}>
                <Box css={{ display: 'flex', flexDirection: 'column', gap: '300' }}>
                  <Paragraph>See how your listing compares to similar properties.</Paragraph>
                  <ToggleButtonGroup
                    aria-label="Competitor ranking metric"
                    size="sm"
                    conjoined={true}
                    value={competitorMetric}
                    onValueChange={(value) => setCompetitorMetric(value as CompetitorMetric)}
                    css={{ display: 'flex', width: '100%' }}
                  >
                    {(Object.keys(competitorMetricLabels) as Array<CompetitorMetric>).map((metric) => (
                      <ToggleButton key={metric} value={metric} css={{ flex: 1 }}>
                        {competitorMetricLabels[metric]}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <Table.Responsive>
                    <Table.Root aria-label="Similar property rankings" appearance="bare">
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Property</Table.HeaderCell>
                          <Table.HeaderCell css={{ textAlign: 'right' }}>Package</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {rankedEntries.map((entry, index) => (
                          <Table.Row
                            key={entry.comp.listingAlias}
                            css={
                              entry.isCurrentListing
                                ? { '& > td': { backgroundColor: 'Table.Cell.bg.zebra' } }
                                : undefined
                            }
                          >
                            <Table.Cell>
                              <Text textStyle={entry.isCurrentListing ? 'body-sm-bold' : 'body-sm'}>
                                {`${index + 1}. ${entry.displayName}`}
                              </Text>
                            </Table.Cell>
                            <Table.Cell css={{ textAlign: 'right' }}>
                              <Tag size="sm" tone={entry.packageTone}>
                                {entry.packageName}
                              </Tag>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Table.Responsive>
                </Box>
                <Card elevated={false} tone="neutral">
                  <Box css={{ display: 'flex', flexDirection: 'column', gap: '300' }}>
                    <Text textStyle="body-bold">Ad packages nearby</Text>
                    <Paragraph>Based on top properties within 5 miles.</Paragraph>
                    <Box css={{ height: '240px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{ top: 24, right: 96, bottom: 24, left: 96 }}>
                          <Pie
                            data={packageDistribution}
                            dataKey="value"
                            nameKey="label"
                            cx="50%"
                            cy="50%"
                            innerRadius="62%"
                            outerRadius="86%"
                            paddingAngle={1}
                            isAnimationActive={false}
                            label={renderPackageLabel}
                            labelLine={false}
                          >
                            {packageDistribution.map((item) => (
                              <Cell key={item.label} fill={item.color} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </Card>
              </Box>
            </Modal.Body>
            <Divider />
            <Modal.Footer>
              <ButtonGroup aria-label="Lead rank actions">
                <Modal.Close>
                  <Button tone="brand" emphasis="bare">
                    Close
                  </Button>
                </Modal.Close>
                <Button tone="brand" emphasis="filled" asChild>
                  <Link href={{ pathname: ROUTES.PREMIUM_ANALYTICS, query: { listingAlias } }}>
                    View competitor insights
                  </Link>
                </Button>
              </ButtonGroup>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
};

export default HigherTierPackagesModal;
